export type ConsentRegion = 'GDPR' | 'CCPA' | 'NONE';

export interface ConsentChoices {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export interface ConsentRecord {
  version: string;
  timestamp: string;
  region: ConsentRegion;
  choices: ConsentChoices;
  userAgent: string;
}

const CONSENT_KEY = 'coloco_consent_v1';
const CONSENT_VERSION = '1.0';

export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentRecord;
  } catch {
    return null;
  }
}

export function saveConsent(region: ConsentRegion, choices: ConsentChoices): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    region,
    choices,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
  if (typeof window !== 'undefined') {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  }
  return record;
}

export function clearConsent(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(CONSENT_KEY);
  }
}

export function hasConsented(): boolean {
  return getConsent() !== null;
}

export function acceptAll(region: ConsentRegion): ConsentRecord {
  return saveConsent(region, {
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true,
  });
}

export function rejectAll(region: ConsentRegion): ConsentRecord {
  return saveConsent(region, {
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
}

export async function detectRegion(): Promise<ConsentRegion> {
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
    const data = await res.json();
    const euCountries = [
      'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
      'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
      'IS','LI','NO','GB','CH',
    ];
    if (euCountries.includes(data.country_code)) return 'GDPR';
    if (data.country_code === 'US' && data.region_code === 'CA') return 'CCPA';
    return 'NONE';
  } catch {
    // Default to GDPR (most restrictive) on failure
    return 'GDPR';
  }
}
